/**
 * QUANTICA · <q-gallery>
 *
 * Galeria de producto: pista con scroll-snap, miniaturas y arrastre.
 * El scroll y el snap los hace el navegador; el JS solo sincroniza las
 * miniaturas y agrega el arrastre con mouse. Sin JS la galeria sigue
 * funcionando: se scrollea con el dedo, con la rueda y con el teclado.
 *
 * Diferencias contra el JS del HTML aprobado:
 * - Las miniaturas son <button> con aria-selected y roles de tablist, no
 *   divs con estilos inline. Se recorren con teclado.
 * - El estado activo se marca con un atributo, no escribiendo estilos
 *   inline desde JS, asi el CSS sigue siendo la unica fuente de verdad.
 * - Se respeta prefers-reduced-motion en el scroll programatico.
 */
class QGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track) return;

    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onScroll = this.onScroll.bind(this);

    this.thumbs.forEach((b, i) => b.addEventListener('click', () => this.goTo(i)));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.initDrag();
    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  get index() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  goTo(i) {
    this.track.scrollTo({
      left: i * this.track.clientWidth,
      behavior: this.reduced ? 'auto' : 'smooth',
    });
  }

  onScroll() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.sync(), 70);
  }

  sync() {
    const i = this.index;
    this.thumbs.forEach((b, j) => {
      const on = j === i;
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
  }

  /** Arrastre con mouse. El tactil ya lo resuelve el scroll nativo. */
  initDrag() {
    let down = false, x0 = 0, s0 = 0;

    this.track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      down = true;
      x0 = e.clientX;
      s0 = this.track.scrollLeft;
      this.track.style.scrollSnapType = 'none';
      this.track.setPointerCapture?.(e.pointerId);
    });

    this.track.addEventListener('pointermove', (e) => {
      if (!down) return;
      this.track.scrollLeft = s0 - (e.clientX - x0);
    });

    const up = () => {
      if (!down) return;
      down = false;
      this.track.style.scrollSnapType = '';
      this.goTo(this.index);
      setTimeout(() => this.sync(), 420);
    };

    this.track.addEventListener('pointerup', up);
    this.track.addEventListener('pointercancel', up);
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
    window.addEventListener('resize', () => this.sync());
  }
}

if (!customElements.get('q-gallery')) {
  customElements.define('q-gallery', QGallery);
}
